import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import {
  onAuthStateChanged,
  User,
  signInAnonymously,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import LinkSaver from './components/LinkSaver';
import AdminApprovalPage from './components/AdminApprovalPage';
import PublicLinksPage from './components/PublicLinksPage';
import type { NavLinkItem } from './components/Header'; // Đảm bảo Header.tsx export NavLinkItem

import './App.css'; // Đảm bảo file này tồn tại nếu bạn có import

function App() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isAdminForNav, setIsAdminForNav] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        if (!user.isAnonymous) {
          const adminDocRef = doc(db, 'admins', user.uid);
          try {
            const adminDocSnap = await getDoc(adminDocRef);
            setIsAdminForNav(adminDocSnap.exists());
          } catch (error) {
            console.error("App.tsx: Lỗi khi kiểm tra trạng thái admin:", error);
            setIsAdminForNav(false);
          }
        } else {
          setIsAdminForNav(false);
        }
      } else {
        try {
          const anonUserCredential = await signInAnonymously(auth);
          setCurrentUser(anonUserCredential.user);
        } catch (error) {
          console.error("App.tsx: Lỗi khi đăng nhập ẩn danh:", error);
          setCurrentUser(null);
        }
        setIsAdminForNav(false);
      }
      setIsLoadingAuth(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSignInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("App.tsx: Lỗi khi đăng nhập với Google:", error);
      alert("Đăng nhập bằng Google thất bại. Vui lòng thử lại.");
    }
  };

  const handleSignOut = async () => {
    try {
      await firebaseSignOut(auth);
      setIsAdminForNav(false); // Reset admin status on sign out
      setCurrentUser(null); // Ensure currentUser is null after sign out
    } catch (error) {
      console.error("App.tsx: Lỗi khi đăng xuất:", error);
    }
  };

  const navLinks: NavLinkItem[] = [
    { to: "/", label: "Trang chủ", show: true },
    { to: "/public", label: "Links Công Khai", show: true },
    { to: "/admin", label: "Trang Admin", show: isAdminForNav },
  ];

  if (isLoadingAuth) {
    return <div className="AppLoading"><p>Đang tải ứng dụng...</p></div>;
  }

  return (
    <BrowserRouter>
      <div className="App">
        <main className="app-content">
          <Routes>
            <Route
              path="/"
              element={
                <LinkSaver
                  userId={currentUser ? currentUser.uid : null}
                  currentUser={currentUser}
                  onSignIn={handleSignInWithGoogle}
                  onSignOut={handleSignOut}
                  navLinks={navLinks}
                />
              }
            />
            <Route
              path="/public"
              element={
                <PublicLinksPage
                  userId={currentUser ? currentUser.uid : null}
                  currentUser={currentUser}
                  onSignIn={handleSignInWithGoogle}
                  onSignOut={handleSignOut}
                  navLinks={navLinks}
                />
              }
            />
            <Route
              path="/admin"
              element={
                isAdminForNav && currentUser && !currentUser.isAnonymous ? (
                  <AdminApprovalPage />
                ) : (
                  <Navigate to="/" replace state={{ message: "Bạn không có quyền truy cập trang Admin." }} />
                )
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
        <footer style={{ textAlign: 'center', padding: '20px', borderTop: '1px solid #eee', marginTop: '30px' }}>
          <p>© {new Date().getFullYear()} Link Saver App</p>
        </footer>
      </div>
    </BrowserRouter>
  );
}

export default App;